import type { WebSocketMessage, RenderResult } from '../types';

type MessageHandler = (data: RenderResult) => void;
type ErrorHandler = (error: string) => void;

export class HelmWebSocket {
  private ws: WebSocket | null = null;
  private sessionId: string;
  private onRenderResult: MessageHandler;
  private onError: ErrorHandler;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private pendingMessages: string[] = [];

  constructor(sessionId: string, onRenderResult: MessageHandler, onError: ErrorHandler) {
    this.sessionId = sessionId;
    this.onRenderResult = onRenderResult;
    this.onError = onError;
    this.connect();
  }

  private connect() {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    this.ws = new WebSocket(`${protocol}//${host}/ws/${this.sessionId}`);

    this.ws.onopen = () => {
      console.log('WebSocket connected');
      this.reconnectAttempts = 0;
      // Send any pending messages
      while (this.pendingMessages.length > 0) {
        const msg = this.pendingMessages.shift();
        if (msg && this.ws?.readyState === WebSocket.OPEN) {
          this.ws.send(msg);
        }
      }
    };

    this.ws.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        this.handleMessage(message);
      } catch (e) {
        console.error('Failed to parse WebSocket message:', e);
      }
    };

    this.ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    this.ws.onclose = () => {
      console.log('WebSocket closed');
      this.tryReconnect();
    };
  }

  private tryReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
      console.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);
      setTimeout(() => this.connect(), delay);
    } else {
      this.onError('WebSocket connection lost. Please refresh the page.');
    }
  }

  private handleMessage(message: WebSocketMessage) {
    switch (message.type) {
      case 'render_result':
        this.onRenderResult(message.payload as RenderResult);
        break;
      case 'error': {
        const errorPayload = message.payload as { message: string };
        this.onError(errorPayload.message);
        break;
      }
      default:
        console.warn('Unknown message type:', message.type);
    }
  }

  private send(message: WebSocketMessage) {
    const msgStr = JSON.stringify(message);
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(msgStr);
    } else {
      this.pendingMessages.push(msgStr);
    }
  }

  updateValues(values: string, releaseName?: string, namespace?: string) {
    this.send({
      type: 'values_update',
      payload: { values, releaseName, namespace },
    });
  }

  updateTemplate(templateName: string, content: string) {
    this.send({
      type: 'template_update',
      payload: { templateName, content },
    });
  }

  requestRender() {
    this.send({
      type: 'render_request',
      payload: {},
    });
  }

  close() {
    this.maxReconnectAttempts = 0; // Prevent reconnection
    this.ws?.close();
  }
}
